import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiCheckCircle, FiHome, FiShield } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { PageHero } from '../components/ui/PageElements';
import { formatCurrency, formatDate } from '../utils/formatters';

const ReservationConfirmationPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { reservationDetails } = location.state || {};

  if (!reservationDetails) {
    return (
      <Layout>
        <div className="content-width py-16">
          <div className="soft-panel flex min-h-[320px] flex-col items-center justify-center gap-5 text-center">
            <h1 className="text-3xl font-semibold text-slate-900">{t('design.reservationConfirmation.noReservation')}</h1>
            <button type="button" onClick={() => navigate('/')} className="btn-primary">
              <FiHome className="h-4 w-4" />
              {t('design.reservationConfirmation.backHome')}
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHero
        eyebrow={t('design.reservationConfirmation.heroEyebrow')}
        title={t('design.reservationConfirmation.heroTitle')}
        description={t('design.reservationConfirmation.heroDescription')}
        aside={
          <div className="soft-panel p-6 sm:p-8">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
              <FiCheckCircle className="h-8 w-8" />
            </div>
            <p className="mt-5 text-xs uppercase tracking-[0.2em] text-stone-500">{t('hotels.confirmation.pnrCode')}</p>
            <p className="mt-2 font-serif text-4xl font-semibold text-slate-900">{reservationDetails.pnrCode}</p>
          </div>
        }
      />

      <section className="content-width pb-12">
        <div className="grid gap-6 lg:grid-cols-[1fr_0.8fr]">
          <div className="soft-panel p-6 sm:p-8">
            <h2 className="text-3xl font-semibold text-slate-900">{t('hotels.confirmation.reservationDetails')}</h2>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              {[
                [t('hotels.confirmation.hotel'), reservationDetails.hotelName],
                [t('hotels.confirmation.guestName'), reservationDetails.guestName],
                [t('hotels.confirmation.email'), reservationDetails.guestEmail],
                [t('hotels.confirmation.checkIn'), formatDate(reservationDetails.checkIn)],
                [t('hotels.confirmation.checkOut'), formatDate(reservationDetails.checkOut)],
                [t('hotels.confirmation.totalAmount'), formatCurrency(reservationDetails.totalAmount)]
              ].map(([label, value]) => (
                <div key={label} className="rounded-[24px] border border-stone-200 bg-stone-50/80 p-5">
                  <p className="text-xs uppercase tracking-[0.2em] text-stone-500">{label}</p>
                  <p className="mt-2 text-base font-semibold text-slate-900">{value || '-'}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            <div className="soft-panel p-6 sm:p-8">
              <div className="flex items-start gap-3">
                <div className="rounded-full bg-teal-50 p-3 text-teal-700">
                  <FiShield className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.2em] text-stone-500">{t('hotels.confirmation.importantInfo')}</p>
                  <ul className="mt-4 space-y-3 text-sm leading-7 text-slate-600">
                    <li>{t('design.reservationConfirmation.notes.keepPnr')}</li>
                    <li>{t('design.reservationConfirmation.notes.bringId')}</li>
                    <li>{t('design.reservationConfirmation.notes.supportVerify')}</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="soft-panel p-6 sm:p-8">
              <button type="button" onClick={() => navigate('/')} className="btn-primary w-full justify-center">
                <FiHome className="h-4 w-4" />
                {t('design.reservationConfirmation.backHome')}
              </button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ReservationConfirmationPage;
